import { pickBy } from "lodash";
import { ChangeEvent, Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { sortFloorLabels, sortNamesBy } from "logic/sortNames";
import { DeviceConfig, DeviceConfigAudioType, DeviceFamily, VoiceSet, VoiceSetId, VolumeType } from 'shared/models';
import { OrderField, LegacySelectField, TextField } from "ui/shared/controls";
import { VoiceOverrideRow } from "./voiceOverrideRow/VoiceOverrideRow";

require('./AudioTab.css');
    
const audioOrderOptionLabelKeys = {
    [DeviceConfigAudioType.Floor]: 'DEVICE_CONFIGS.DIALOG.FIELD.FLOOR',
    [DeviceConfigAudioType.Direction]: 'DEVICE_CONFIGS.DIALOG.FIELD.DIRECTION',
    [DeviceConfigAudioType.Gong]: 'DEVICE_CONFIGS.DIALOG.FIELD.GONG',
};

const defaultVolumes = {
    [VolumeType.Background]: 0,
    [VolumeType.Buzzer]: 5,
    [VolumeType.Chime]: 5,
    [VolumeType.Direction]: 0,
    [VolumeType.Floor]: 5,
    [VolumeType.FloorPassing]: 5,
    [VolumeType.ServiceMessage]: 5,
    [VolumeType.Master]: 2,
    [VolumeType.Media]: 2,
    [VolumeType.Synth]: 2,
};

const nullValue = 'none';

export type Props = {
    config: DeviceConfig;
    readOnly?: boolean;
    voiceSets: Record<VoiceSetId, VoiceSet>;
    deleteOnValue: string;
    onChange(e: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void;
    setData: Dispatch<SetStateAction<DeviceConfig>>;
};

export const AudioTab = ({ deleteOnValue, setData, config, readOnly = false, voiceSets, onChange }: Props) => {
    const { t } = useTranslation();

    const voiceOverrides = config.properties.voiceOverrides;
    const voiceSet = (config.voiceSetId && voiceSets[config.voiceSetId]) || null;

    // The floor markings that can be overridden are those with multiple voice file options
    const overridableFloorMarkings =
        voiceSet
            ? Object.keys(pickBy(voiceSet.mappings, voiceFiles => voiceFiles.length > 1))
            : [];
    
    const isDeviceFamilyTTG = config.properties.deviceFamily === DeviceFamily.TTG;
    const isDeviceFamilyMR4 = config.properties.deviceFamily === DeviceFamily.MatisseR4;
    const isDeviceFamilyGiotto = config.properties.deviceFamily === DeviceFamily.Giotto;
    const isDeviceFamilyR5Plus = config.properties.deviceFamily === DeviceFamily.MatisseR5
        || config.properties.deviceFamily === DeviceFamily.Giotto
        || config.properties.deviceFamily === DeviceFamily.Raffaello
        || config.properties.deviceFamily === DeviceFamily.MatisseFlexR2;
    
    const maxVolume = isDeviceFamilyGiotto ? 10 : 5;

    const volumeKeys = isDeviceFamilyGiotto
        ? [
            VolumeType.Chime,
            VolumeType.Background,
            VolumeType.Buzzer,
            VolumeType.Direction,
            VolumeType.ServiceMessage,
            VolumeType.Floor,
            VolumeType.FloorPassing,
        ]
        : [
            VolumeType.Master,
            VolumeType.Media,
            VolumeType.Synth,
        ];
    
    const handleChangeOrder = (data: DeviceConfigAudioType[]) => {
        setData({ ...config,
            properties: {
                ...config.properties,
                audioOrder: data,
            },
        });
    };

    return (
        <div id="audio-tab">
            <LegacySelectField
                className="_set"
                id="device-config-voice-set-id"
                labelKey="LABEL.VOICE_SET"
                readOnly={ readOnly }
                input={ {
                    name: "voiceSetId",
                    value: config.voiceSetId || nullValue,
                    onChange,
                } }
            >
                <option value={ nullValue }>
                    { t('NONE') }
                </option>
                { Object.values(voiceSets).sort(sortNamesBy(vs => vs.displayName)).map(voiceSet =>
                    <option
                        key={ voiceSet.id }
                        value={ voiceSet.id }
                    >
                        { voiceSet.displayName }
                    </option>
                ) }
            </LegacySelectField>

            <div className={ `_volume field` }>
                <label>
                    { t(`LABEL.VOLUME`) }
                </label>
                <div className="_volume-container">
                    { volumeKeys.map(volumeParamKey => {
                        const fieldId = `device-config-${volumeParamKey}`;
                        const labelKey = `DEVICE_CONFIGS.DIALOG.FIELD.${volumeParamKey.replace(/([a-z])([A-Z])/g, '$1_$2').toUpperCase()}`;
                        return (
                            <TextField
                                type="number"
                                min={ 0 }
                                max={ maxVolume }
                                id={ fieldId }
                                key={ volumeParamKey }
                                labelKey={ labelKey }
                                disabled={ isDeviceFamilyTTG || isDeviceFamilyMR4 }
                                readOnly={ readOnly }
                                name={ `properties.${volumeParamKey}` }
                                value={
                                    config.properties[volumeParamKey] !== undefined
                                        ? config.properties[volumeParamKey]
                                        : (defaultVolumes[volumeParamKey] || 0)
                                }
                                onChange={ onChange }
                            />
                        );
                    })}
                </div>
            </div>

            {isDeviceFamilyR5Plus &&
                <OrderField
                    id="device-config-audio-order"
                    className="_order"
                    labelKey={ 'DEVICE_CONFIGS.DIALOG.FIELD.AUDIO_ORDER' }
                    optionLabelKeys={ audioOrderOptionLabelKeys }
                    alwaysChecked={ isDeviceFamilyGiotto }
                    unorderableOptions={ isDeviceFamilyGiotto ? [ DeviceConfigAudioType.Direction ] : [] }
                    value={ config.properties.audioOrder }
                    onChange={ handleChangeOrder }
                />
            }

            <div className={ `_overrides field ${voiceSet ? '' : 'disabled'}` }>
                <label>
                    { t('DEVICE_CONFIGS.DIALOG.FIELD.OVERRIDES') }
                </label>
                <div className="_override-container">
                    { voiceSet && overridableFloorMarkings
                        .sort(sortFloorLabels)
                        .map(floorMarking =>
                            <VoiceOverrideRow
                                voiceSetId={ voiceSet.id }
                                key={ floorMarking }
                                floorMarking={ floorMarking }
                                filename={ voiceOverrides[floorMarking] }
                                voiceFiles={ voiceSet.mappings[floorMarking] }
                                readOnly={ readOnly }
                                deleteOnValue={ deleteOnValue }
                                onChange={ onChange }
                            />
                        )
                    }
                </div>
            </div>
        </div>
    );
};
